import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./AdminReports.scss";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../../awsAuth";
import DynamicHeader from "../../Header/DynamicHeader";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Відкриті" },
  { value: "RESOLVED", label: "Повернені у видимість" },
  { value: "CLOSED_REMOVED", label: "Видалені модерацією" },
];

const FALLBACK_IMG =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/placeholder.png";

const RESTORE_THRESHOLD = 5; // >5

function FiltersBar({ status, setStatus, busy, reload, counts }) {
  const getSubtitle = (value) => {
    if (value === "OPEN") return "Активні скарги, що чекають рішення";
    if (value === "RESOLVED") return "Контент повернуто у видимість";
    if (value === "CLOSED_REMOVED") return "Сховано або видалено модерацією";
    return "";
  };

  return (
    <div className="ar__filters">
      <div className="ar__cards-row">
        {STATUS_OPTIONS.map((o) => {
          const isActive = status === o.value;
          const count = counts?.[o.value];
          return (
            <button
              key={o.value}
              type="button"
              className={`ar-card ar-card--${o.value.toLowerCase()} ${
                isActive ? "is-active" : ""
              }`}
              disabled={busy && !isActive}
              onClick={() => {
                if (!isActive) setStatus(o.value);
              }}
            >
              <div className="ar-card__label">{o.label}</div>
              <div className="ar-card__value">
                {typeof count === "number" ? count : "—"}
              </div>
              <div className="ar-card__subtitle">{getSubtitle(o.value)}</div>
            </button>
          );
        })}
      </div>

      <button
        className="ar__btn ar__btn--black ar__btn--refresh"
        disabled={busy}
        onClick={reload}
        aria-label="Оновити список"
      >
        Оновити
      </button>
    </div>
  );
}

/* Utils */
function formatTs(ts) {
  try {
    if (!ts) return "";
    const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
}

function topReasons(reasonsCount = {}, max = 3) {
  return Object.entries(reasonsCount)
    .filter(([k, v]) => k && typeof v === "number")
    .sort((a, b) => b[1] - a[1])
    .slice(0, max);
}

/* Component */
export default function AdminReports() {
  const navigate = useNavigate();

  const [status, setStatus] = useState("OPEN");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [isStaff, setIsStaff] = useState(false);

  const [previews, setPreviews] = useState({});
  const [aggregates, setAggregates] = useState({});
  const [counts, setCounts] = useState({});

  const getAuthHeaders = useCallback(async (extra = {}) => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error("NO_ACCESS_TOKEN");
    }

    return {
      ...extra,
      Authorization: `Bearer ${token}`,
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const headers = await getAuthHeaders();
        const r = await fetch(`${API}/api/admin/me`, {
          headers,
          cache: "no-store",
        });

        const j = await r.json().catch(() => ({}));
        const ok = !!(j?.data?.isAdmin || j?.data?.isMod || j?.isAdmin || j?.isMod);

        if (alive) {
          setIsStaff(ok);
        }
      } catch {
        if (alive) {
          setIsStaff(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [getAuthHeaders]);

  const title = useMemo(() => {
    const opt =
      STATUS_OPTIONS.find((o) => o.value === status)?.label || "Скарги";
    return `Скарги — ${opt}`;
  }, [status]);

  const fetchAggregateOne = useCallback(
    async (type, id) => {
      try {
        const headers = await getAuthHeaders();
        const r = await fetch(
          `${API}/api/reports/targets/${encodeURIComponent(
            type
          )}/${encodeURIComponent(id)}`,
          {
            headers,
            cache: "no-store",
          }
        );

        if (!r.ok) throw new Error("AGG_FETCH_FAILED");

        const j = await r.json().catch(() => ({}));
        const agg = j?.aggregate || null;

        if (!agg) {
          return {
            totalCount: 0,
            autoHidden: false,
            reasonsCount: {},
            lastReportAt: null,
          };
        }

        return {
          totalCount: Number(agg.totalCount || 0),
          autoHidden: !!agg.autoHidden,
          reasonsCount: agg.reasonsCount || {},
          lastReportAt: agg.lastReportAt || null,
        };
      } catch {
        return {
          totalCount: 0,
          autoHidden: false,
          reasonsCount: {},
          lastReportAt: null,
        };
      }
    },
    [getAuthHeaders]
  );

  const fetchAggregatesBatch = useCallback(
    async (targets) => {
      const out = {};

      await Promise.all(
        targets.map(async ({ type, id }) => {
          const key = `${type}#${id}`;
          out[key] = await fetchAggregateOne(type, id);
        })
      );

      return out;
    },
    [fetchAggregateOne]
  );

  const fetchOnePreview = useCallback(async (type, id) => {
    if (type === "listing") {
      const paths = [
        `${API}/api/listings/${encodeURIComponent(id)}`,
        `${API}/api/listing/${encodeURIComponent(id)}`,
      ];

      for (const url of paths) {
        try {
          const r = await fetch(url, { cache: "no-store" });
          if (!r.ok) continue;

          const d = await r.json().catch(() => ({}));
          if (!d) continue;

          const photos = Array.isArray(d.photos) ? d.photos : [];
          const photo = d.mainPhoto || photos[0] || FALLBACK_IMG;

          return {
            type: "listing",
            id,
            title: d.title || "",
            photo,
            authorName: d.authorName || d.userName || "",
            authorId: d.userId || d.ownerId || d.authorId || "",
            createdAt: d.createdAt || d.ts || "",
          };
        } catch {}
      }

      return {
        type: "listing",
        id,
        title: "",
        photo: FALLBACK_IMG,
        authorName: "",
        authorId: "",
        createdAt: "",
      };
    }

    if (type === "post") {
      const paths = [
        `${API}/api/posts/${encodeURIComponent(id)}`,
        `${API}/api/post/${encodeURIComponent(id)}`,
      ];

      for (const url of paths) {
        try {
          const r = await fetch(url, { cache: "no-store" });
          if (!r.ok) continue;

          const d = await r.json().catch(() => ({}));
          if (!d) continue;

          const photo =
            d.photo ||
            (Array.isArray(d.photos) ? d.photos[0] : "") ||
            FALLBACK_IMG;

          const titleText =
            d.title || (d.text ? String(d.text).slice(0, 80) : "");

          return {
            type: "post",
            id,
            title: titleText,
            photo,
            authorName: d.authorName || d.userName || "",
            authorId: d.userId || d.authorId || "",
            createdAt: d.createdAt || d.ts || "",
          };
        } catch {}
      }

      return {
        type: "post",
        id,
        title: "",
        photo: FALLBACK_IMG,
        authorName: "",
        authorId: "",
        createdAt: "",
      };
    }

    if (type === "user") {
      const paths = [
        `${API}/api/account/${encodeURIComponent(id)}`,
        `${API}/api/users/${encodeURIComponent(id)}`,
      ];

      for (const url of paths) {
        try {
          const r = await fetch(url, { cache: "no-store" });
          if (!r.ok) continue;

          const u = await r.json().catch(() => ({}));
          if (!u) continue;

          return {
            type: "user",
            id,
            title: u.username || u.displayName || `Користувач ${id}`,
            photo: u.avatar || FALLBACK_IMG,
            authorName: u.username || u.displayName || "",
            authorId: id,
            createdAt: u.createdAt || u.ts || "",
          };
        } catch {}
      }

      return {
        type: "user",
        id,
        title: `Користувач ${id}`,
        photo: FALLBACK_IMG,
        authorName: "",
        authorId: id,
        createdAt: "",
      };
    }

    return {
      type,
      id,
      title: "",
      photo: FALLBACK_IMG,
      authorName: "",
      authorId: "",
      createdAt: "",
    };
  }, []);

  const fetchPreviewsBatch = useCallback(
    async (targets) => {
      const results = {};

      await Promise.all(
        targets.map(async ({ type, id }) => {
          const key = `${type}#${id}`;
          try {
            const preview = await fetchOnePreview(type, id);
            results[key] = preview;
          } catch {
            results[key] = {
              type,
              id,
              title: "",
              photo: FALLBACK_IMG,
              authorName: "",
              authorId: "",
              createdAt: "",
            };
          }
        })
      );

      return results;
    },
    [fetchOnePreview]
  );

  const load = useCallback(
    async (explicitStatus) => {
      const currentStatus = explicitStatus || status;
      setBusy(true);
      setErr("");

      try {
        const headers = await getAuthHeaders();

        const r = await fetch(
          `${API}/api/reports/admin?status=${encodeURIComponent(
            currentStatus
          )}&limit=100`,
          {
            headers,
            cache: "no-store",
          }
        );

        const j = await r.json().catch(() => ({}));

        if (!r.ok || !j?.ok) {
          throw new Error(j?.error || "LOAD_FAILED");
        }

        const list = Array.isArray(j.items) ? j.items : [];
        setItems(list);

        setCounts((prev) => ({
          ...prev,
          [currentStatus]: list.length || 0,
        }));

        const missingPrev = [];
        const missingAgg = [];
        const seenP = new Set(Object.keys(previews));
        const seenA = new Set(Object.keys(aggregates));

        for (const it of list) {
          const pk = String(it?.PK || "");
          const [, tTypeFromPK, tIdFromPK] = pk.split("#");
          const type = (it?.targetType || tTypeFromPK || "").toLowerCase();
          const id = it?.targetId || tIdFromPK;

          if (!type || !id) continue;

          const key = `${type}#${id}`;

          if (!seenP.has(key)) missingPrev.push({ type, id });
          if (!seenA.has(key)) missingAgg.push({ type, id });
        }

        if (missingPrev.length) {
          const fetched = await fetchPreviewsBatch(missingPrev);
          setPreviews((prev) => ({ ...prev, ...fetched }));
        }

        if (missingAgg.length) {
          const fetchedAgg = await fetchAggregatesBatch(missingAgg);
          setAggregates((prev) => ({ ...prev, ...fetchedAgg }));
        }
      } catch (e) {
        setErr(e?.message || "Помилка завантаження");
      } finally {
        setBusy(false);
      }
    },
    [status, previews, aggregates, getAuthHeaders, fetchPreviewsBatch, fetchAggregatesBatch]
  );

  useEffect(() => {
    if (isStaff) {
      load();
    }
  }, [isStaff, load]);

  useEffect(() => {
    if (isStaff) {
      load(status);
    }
  }, [status, isStaff, load]);

  const handleResolve = async (type, id) => {
    if (
      !window.confirm(
        type === "user"
          ? "Зняти обмеження та відновити акаунт?"
          : "Повернути у видимість (RESOLVED)?"
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      const headers = await getAuthHeaders({
        "Content-Type": "application/json",
      });

      const r = await fetch(
        `${API}/api/reports/targets/${encodeURIComponent(
          type
        )}/${encodeURIComponent(id)}/resolve`,
        {
          method: "POST",
          headers,
        }
      );

      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j?.ok) {
        throw new Error(j?.error || "RESOLVE_FAILED");
      }

      await load();

      alert(
        type === "user"
          ? "✅ Акаунт відновлено (користувач отримав сповіщення)"
          : "✅ Повернено у видимість (автор отримав сповіщення)"
      );
    } catch (e) {
      alert("⚠️ " + (e?.message || "Помилка"));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (type, id) => {
    if (
      !window.confirm(
        type === "user"
          ? "Видалити акаунт користувача? Це незворотно."
          : "Видалити контент? Це незворотно."
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      const body = {
        reason: "OTHER",
        message:
          type === "user"
            ? "Видалення акаунта модерацією"
            : "Модераційне видалення",
      };

      const headers = await getAuthHeaders({
        "Content-Type": "application/json",
      });

      const r = await fetch(
        `${API}/api/reports/targets/${encodeURIComponent(
          type
        )}/${encodeURIComponent(id)}/remove`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        }
      );

      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j?.ok) {
        throw new Error(j?.error || "REMOVE_FAILED");
      }

      await load();

      alert(
        type === "user"
          ? "✅ Акаунт видалено модерацією (користувач отримав сповіщення)"
          : "✅ Видалено модерацією (автор отримав сповіщення)"
      );
    } catch (e) {
      alert("⚠️ " + (e?.message || "Помилка"));
    } finally {
      setBusy(false);
    }
  };

  const handleWarnUser = async (id) => {
    const note = window.prompt("Текст попередження (необовʼязково):", "");
    if (note === null) return;

    setBusy(true);

    try {
      const headers = await getAuthHeaders({
        "Content-Type": "application/json",
      });

      const r = await fetch(
        `${API}/api/reports/targets/user/${encodeURIComponent(id)}/warn`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ message: note || "" }),
        }
      );

      const j = await r.json().catch(() => ({}));

      if (!r.ok || !j?.ok) {
        throw new Error(j?.error || "WARN_FAILED");
      }

      await load();

      alert(
        "✅ Попередження надіслано, відкриті скарги позначено як переглянуті"
      );
    } catch (e) {
      alert("⚠️ " + (e?.message || "Помилка"));
    } finally {
      setBusy(false);
    }
  };

  const handleWarnAuthor = async (authorId) => {
    if (!authorId) {
      alert("⚠️ Немає authorId — неможливо надіслати попередження.");
      return;
    }

    await handleWarnUser(authorId);
  };

  const openTarget = (preview) => {
    if (!preview) return;

    if (preview.type === "listing") {
      navigate(`/listing/${encodeURIComponent(preview.id)}`);
    } else if (preview.type === "post") {
      const author = preview.authorId || "unknown";
      navigate(
        `/profile/${encodeURIComponent(
          author
        )}?tab=posts&item=${encodeURIComponent(preview.id)}`
      );
    } else if (preview.type === "user") {
      navigate(`/profile/${encodeURIComponent(preview.id)}`);
    }
  };

  return (
    <>
      <DynamicHeader
        title="Модерація"
        variant="glass"
        size="regular"
        showBack
        onBack={() => navigate("/settings")}
        withSpacer
      />

      {!isStaff ? (
        <div className="admin-reports">
          <div className="ar__guard is-glass">
            <h2>Доступ обмежено</h2>
            <p>Цей розділ доступний лише модераторам та адміністраторам.</p>
            <button
              className="ar__btn ar__btn--black"
              onClick={() => navigate("/settings")}
            >
              Повернутись до налаштувань
            </button>
          </div>
        </div>
      ) : (
        <div className="admin-reports">
          <div className="ar__header is-glass">
            <FiltersBar
              status={status}
              setStatus={setStatus}
              busy={busy}
              reload={() => load(status)}
              counts={counts}
            />
          </div>

          <div className="ar__summary">
            <span>{title}</span>
            <b>{items?.length || 0}</b>
          </div>

          {err && <div className="ar__error">⚠️ {err}</div>}

          <div className="ar__table" role="table" aria-label="Список скарг">
            <div className="ar__thead" role="row">
              <div className="th th--preview" role="columnheader">
                Прев’ю
              </div>
              <div className="th th--title" role="columnheader">
                Заголовок
              </div>
              <div className="th th--type" role="columnheader">
                Тип / ID
              </div>
              <div className="th th--author" role="columnheader">
                Автор
              </div>
              <div className="th th--reporter" role="columnheader">
                Репортер
              </div>
              <div className="th th--reason" role="columnheader">
                Скарги
              </div>
              <div className="th th--status" role="columnheader">
                Статус
              </div>
              <div className="th th--actions" role="columnheader">
                Дії
              </div>
            </div>

            {(items || []).map((it, idx) => {
              const pk = String(it?.PK || "");
              const [, tTypeFromPK, tIdFromPK] = pk.split("#");
              const type = (it?.targetType || tTypeFromPK || "?").toLowerCase();
              const id = it?.targetId || tIdFromPK || "?";
              const key = `${type}#${id}`;

              const p =
                previews[key] || {
                  type,
                  id,
                  title: "",
                  photo: FALLBACK_IMG,
                  authorName: "",
                  authorId: "",
                  createdAt: "",
                };

              const agg =
                aggregates[key] || {
                  totalCount: 0,
                  autoHidden: false,
                  reasonsCount: {},
                  lastReportAt: null,
                };

              const statusNow = it?.status || "OPEN";
              const reason = (it?.reason || "").toUpperCase();
              const repId = it?.reporterId || "—";

              const typeBadge =
                type === "listing"
                  ? "Оголошення"
                  : type === "post"
                  ? "Пост"
                  : type === "user"
                  ? "Користувач"
                  : type;

              const canRestoreByCount = (agg.totalCount || 0) > RESTORE_THRESHOLD;
              const canResolve = !!(agg.autoHidden || canRestoreByCount);

              const reasonsTop = topReasons(agg.reasonsCount, 3);

              return (
                <div
                  key={`${pk}:${it?.SK || it?.reportId || idx}`}
                  className="ar__trow"
                  role="row"
                  style={{ animationDelay: `${Math.min(idx, 8) * 25}ms` }}
                  data-auto-hidden={agg.autoHidden ? "1" : "0"}
                >
                  <div className="td td--preview" role="cell">
                    <button
                      className="ar__thumb"
                      onClick={() => openTarget(p)}
                      title="Відкрити"
                    >
                      <img
                        src={p.photo || FALLBACK_IMG}
                        alt={p.title || `${p.type} ${p.id}`}
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMG;
                        }}
                      />
                    </button>
                  </div>

                  <div className="td td--title" role="cell">
                    <div className="ar__title" title={p.title || ""}>
                      {p.title ||
                        (type === "user"
                          ? "Профіль користувача"
                          : "Без назви")}
                    </div>
                    {p.createdAt && (
                      <div className="ar__subtitle">{formatTs(p.createdAt)}</div>
                    )}
                  </div>

                  <div className="td td--type" role="cell">
                    <span
                      className={`ar-badge ${
                        type === "listing"
                          ? "is-listing"
                          : type === "post"
                          ? "is-post"
                          : type === "user"
                          ? "is-user"
                          : ""
                      }`}
                    >
                      {typeBadge}
                    </span>
                    <div className="ar-id">#{p.id}</div>
                  </div>

                  <div className="td td--author" role="cell">
                    {type === "user" ? (
                      <button
                        className="linklike"
                        onClick={() =>
                          navigate(`/profile/${encodeURIComponent(p.id)}`)
                        }
                        title={p.authorName || ""}
                      >
                        {p.authorName || "Користувач"}
                      </button>
                    ) : (
                      <button
                        className="linklike"
                        onClick={() =>
                          p.authorId
                            ? navigate(`/profile/${encodeURIComponent(p.authorId)}`)
                            : null
                        }
                        disabled={!p.authorId}
                        title={p.authorName || ""}
                      >
                        {p.authorName || "Автор"}
                      </button>
                    )}
                  </div>

                  <div className="td td--reporter" role="cell">
                    <span className="mono">{repId}</span>
                  </div>

                  <div className="td td--reason" role="cell">
                    <div className="ar__agg">
                      <span
                        className={`ar-chip ar-chip--agg ${
                          agg.autoHidden ? "is-autohidden" : ""
                        }`}
                        title={
                          agg.autoHidden
                            ? "Автоматично приховано"
                            : `Скарг: ${agg.totalCount || 0}`
                        }
                      >
                        Скарг: {agg.totalCount || 0}
                      </span>

                      {agg.autoHidden && (
                        <span className="ar-flag ar-flag--hidden">
                          автоприховано
                        </span>
                      )}

                      {!agg.autoHidden && canRestoreByCount && (
                        <span className="ar-flag ar-flag--hidden">
                          перевищено {RESTORE_THRESHOLD}
                        </span>
                      )}
                    </div>

                    {reasonsTop.length > 0 ? (
                      <div className="ar__reasons">
                        {reasonsTop.map(([name, cnt]) => (
                          <span key={name} className="ar-reason">
                            {name.toUpperCase()} × {cnt}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="ar__reason-single">{reason || "—"}</div>
                    )}

                    {agg.lastReportAt && (
                      <div className="ar__subtitle">
                        остання: {formatTs(agg.lastReportAt)}
                      </div>
                    )}
                  </div>

                  <div className="td td--status" role="cell">
                    <span
                      className={`ar-chip ar-chip--status ar-chip--${statusNow.toLowerCase()}`}
                    >
                      {statusNow === "OPEN"
                        ? "Відкрито"
                        : statusNow === "RESOLVED"
                        ? "Повернено"
                        : statusNow === "CLOSED_REMOVED"
                        ? "Видалено"
                        : statusNow}
                    </span>
                  </div>

                  <div className="td td--actions" role="cell">
                    {type === "user" ? (
                      <>
                        {statusNow === "OPEN" ? (
                          <>
                            {canResolve && (
                              <button
                                className="ar__btn ar__btn--black"
                                disabled={busy}
                                onClick={() => handleResolve(type, id)}
                              >
                                Відновити акаунт
                              </button>
                            )}

                            <button
                              className="ar__btn ar__btn--black ghost"
                              disabled={busy}
                              onClick={() => handleWarnUser(id)}
                              title="Надіслати попередження"
                            >
                              Попередити
                            </button>

                            <button
                              className="ar__btn ar__btn--black danger"
                              disabled={busy}
                              onClick={() => handleRemove(type, id)}
                            >
                              Видалити акаунт
                            </button>

                            {!canResolve && (
                              <div className="ar__hint ar__hint--muted">
                                “Відновити” доступно, коли скарг &gt;{" "}
                                {RESTORE_THRESHOLD} або об’єкт автоприховано. Зараз
                                доступні: попередження/видалення.
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <button
                              className="ar__btn ar__btn--black ghost"
                              disabled={busy}
                              onClick={() => handleResolve(type, id)}
                              title="Позначити як переглянуто / актуалізувати"
                            >
                              Позначити «переглянуто»
                            </button>

                            <button
                              className="ar__btn ar__btn--black ghost"
                              disabled={busy}
                              onClick={() => handleWarnUser(id)}
                              title="Надіслати попередження"
                            >
                              Попередити
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {statusNow === "OPEN" ? (
                          <>
                            {canResolve && (
                              <button
                                className="ar__btn ar__btn--black"
                                disabled={busy}
                                onClick={() => handleResolve(type, id)}
                              >
                                Повернути у видимість
                              </button>
                            )}

                            <button
                              className="ar__btn ar__btn--black ghost"
                              disabled={busy || !p.authorId}
                              onClick={() => handleWarnAuthor(p.authorId)}
                              title={
                                p.authorId
                                  ? "Надіслати попередження автору"
                                  : "Немає authorId"
                              }
                            >
                              Попередити автора
                            </button>

                            <button
                              className="ar__btn ar__btn--black danger"
                              disabled={busy}
                              onClick={() => handleRemove(type, id)}
                            >
                              Видалити
                            </button>

                            {!canResolve && (
                              <div className="ar__hint ar__hint--muted">
                                “Повернути у видимість” показуємо, коли скарг &gt;{" "}
                                {RESTORE_THRESHOLD} або об’єкт автоприховано. Зараз
                                доступні: попередження автора / видалення.
                              </div>
                            )}
                          </>
                        ) : (
                          <button
                            className="ar__btn ar__btn--black ghost"
                            disabled={busy}
                            onClick={() => handleResolve(type, id)}
                            title="Позначити як переглянуто/актуалізувати статус"
                          >
                            Позначити «переглянуто»
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {(!items || items.length === 0) && (
              <div className="ar__empty">Немає записів.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}